import React from 'react';
import { CheckCircle, AlertTriangle, CircleSlash } from 'lucide-react';

interface PaymentProgressIndicatorProps {
  totalInvoiced: number;
  totalPaid: number;
  paymentPercentage: number;
  showProgressBar?: boolean;  // Optional visual progress bar
  compact?: boolean;          // Compact mode for grid cells
}

export const PaymentProgressIndicator: React.FC<PaymentProgressIndicatorProps> = ({
  totalInvoiced,
  totalPaid,
  paymentPercentage,
  showProgressBar = false,
  compact = false
}) => {
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Determine color scheme based on payment percentage
  const getColorScheme = () => {
    if (paymentPercentage >= 100) {
      return {
        textColor: 'text-green-600',
        bgColor: 'bg-green-100',
        icon: <CheckCircle className="h-4 w-4" />,
        iconColor: 'text-green-600'
      };
    } else if (paymentPercentage > 0) {
      // Align with invoice 'Partial' amber palette
      return {
        textColor: 'text-amber-700',
        bgColor: 'bg-amber-100',
        icon: <AlertTriangle className="h-4 w-4" />,
        iconColor: 'text-amber-700'
      };
    } else {
      return {
        textColor: 'text-gray-600',
        bgColor: 'bg-gray-100',
        icon: <CircleSlash className="h-4 w-4" />,
        iconColor: 'text-gray-600'
      };
    }
  };

  const colorScheme = getColorScheme();

  if (compact) {
    // Compact mode for grid cells
    return (
      <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium ${colorScheme.bgColor} ${colorScheme.textColor}`}>
        {colorScheme.icon}
        <span>
          {formatCurrency(totalPaid)} / {formatCurrency(totalInvoiced)} ({paymentPercentage.toFixed(0)}%)
        </span>
      </div>
    );
  }

  if (showProgressBar) {
    // Full mode with progress bar
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900">Payment Progress</h3>
          <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium ${colorScheme.bgColor} ${colorScheme.textColor}`}>
            {colorScheme.icon}
            <span>{paymentPercentage.toFixed(1)}%</span>
          </div>
        </div>
        
        <div className="mb-3">
          <div className="text-sm text-gray-600 mb-1">
            {formatCurrency(totalPaid)} paid of {formatCurrency(totalInvoiced)}
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                paymentPercentage >= 100 ? 'bg-green-500' :
                paymentPercentage > 0 ? 'bg-amber-500' : 'bg-gray-400'
              }`}
              style={{ width: `${Math.min(paymentPercentage, 100)}%` }}
            />
          </div>
        </div>
        
        <div className="text-sm text-gray-600">
          {formatCurrency(totalInvoiced - totalPaid)} outstanding
        </div>
      </div>
    );
  }

  // Default mode (simple display)
  return (
    <div className={`inline-flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium ${colorScheme.bgColor} ${colorScheme.textColor}`}>
      {colorScheme.icon}
      <span>
        {formatCurrency(totalPaid)} / {formatCurrency(totalInvoiced)} ({paymentPercentage.toFixed(1)}%)
      </span>
    </div>
  );
};

export default PaymentProgressIndicator;

